import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { client } from "@/data/client";
import { KpiCard } from "@/components/KpiCard";
import { MultiSeriesChart } from "@/components/MultiSeriesChart";
import { formatDelta } from "@/lib/utils";
import { ActivityDaySection } from "@/pages/dashboard-sections/ActivityDaySection";
import type { ActivityStatus } from "@/data/types";

type Period = { periodStart: string; periodEnd: string };

const SERIES = [
  { key: "scheduled", name: "Programadas", color: "hsl(var(--chart-1))" },
  { key: "completed", name: "Finalizadas", color: "hsl(var(--chart-4))" },
  { key: "missed", name: "Não realizadas", color: "hsl(var(--semantic-danger))" },
  { key: "onTime", name: "No prazo", color: "hsl(var(--chart-3))" },
];

export function ActivitiesTab({
  period,
  onOpenStatus,
}: {
  period: Period;
  onOpenStatus: (status: ActivityStatus) => void;
}) {
  const [selectedDay, setSelectedDay] = useState<string | null>(null);

  const kpis = useQuery({ queryKey: ["activity-kpis", period], queryFn: () => client.getActivityKpis(period) });
  const daily = useQuery({ queryKey: ["activity-daily", period], queryFn: () => client.getActivityDaily(period) });

  const data = kpis.data;

  const handleChartClick = (payload: Record<string, unknown>) => {
    const date = payload?.date;
    if (typeof date === "string") setSelectedDay(date);
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-4 gap-5">
        <KpiCard
          label="Atividades programadas"
          value={data ? data.scheduled.toString() : "—"}
          delta={data ? formatDelta(data.scheduledDeltaPct) : undefined}
          deltaDir={data && data.scheduledDeltaPct >= 0 ? "up" : "down"}
          caption="vs período anterior"
          accent="hsl(var(--chart-1))"
          onClick={() => onOpenStatus("scheduled")}
        />
        <KpiCard
          label="Atividades finalizadas"
          value={data ? data.completed.toString() : "—"}
          delta={data ? formatDelta(data.completedDeltaPct) : undefined}
          deltaDir={data && data.completedDeltaPct >= 0 ? "up" : "down"}
          caption="vs período anterior"
          accent="hsl(var(--chart-4))"
          onClick={() => onOpenStatus("completed")}
        />
        <KpiCard
          label="Atividades não realizadas"
          value={data ? data.missed.toString() : "—"}
          caption="programadas sem execução"
          accent="hsl(var(--semantic-danger))"
          onClick={() => onOpenStatus("missed")}
        />
        <KpiCard
          label="Atividades no prazo"
          value={data ? data.onTime.toString() : "—"}
          caption="dentro do SLA combinado"
          accent="hsl(var(--chart-3))"
          onClick={() => onOpenStatus("on-time")}
        />
      </div>

      <MultiSeriesChart
        data={daily.data ?? []}
        series={SERIES}
        title="Visão geral"
        subtitle="Atividades por dia · clique num dia para ver a listagem completa"
        defaultMode="line"
        allowModeToggle={false}
        onPointClick={handleChartClick}
        pulseAllSeries
      />

      <ActivityDaySection date={selectedDay} onClear={() => setSelectedDay(null)} />
    </div>
  );
}
